<template>
  <v-card
    class="card"
    :loading="loading && appColor.secondary"
    :disabled="loading"
    elevation="0"
    @drop.prevent="onFilesDrop"
    @dragover.prevent="dragover = true"
    @dragenter.prevent="dragover = true"
    @dragleave.prevent="dragover = false"
  >
    <v-toolbar
      flat
      class="card__toolbar pl-5 pr-3"
    >
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            icon
            v-on="on"
            @click="$emit('uploadClick')"
          >
            <v-icon
              class="mt-1"
              size="26"
              :style="{ 'color': appColor.icon }"
            >
              bi-cloud-upload
            </v-icon>
          </v-btn>
        </template>
        <span>Загрузить файлы</span>
      </v-tooltip>
      <v-spacer />
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            icon
            class="card__trash-btn"
            v-on="on"
            @click="$emit('deleteAllClick')"
          >
            <v-icon :style="{ 'color': appColor.icon }">
              bi-trash
            </v-icon>
          </v-btn>
        </template>
        <span>Удалить все файлы</span>
      </v-tooltip>
      <v-toolbar-title
        class="card__title"
        :style="{ 'color': appColor.textDark }"
      >
        Загруженные документы
      </v-toolbar-title>
    </v-toolbar>
    <v-list
      class="card__list"
      :class="{
        'card__list--small': $attrs.hasOwnProperty('small'),
        'card__list--medium': $attrs.hasOwnProperty('medium'),
        'card__list--auto': $attrs.hasOwnProperty('auto')
      }"
      :elevation="dragover ? 6 : 0"
    >
      <v-list-item
        v-for="doc in reversedList"
        :inactive="$attrs.hasOwnProperty('inactive')"
        :key="doc.id"
        class="pr-3 pl-6"
        :class="{ 'card__item--active': doc.id === selectedId }"
        two-line
        @click="$emit('documentClick', doc.id)"
      >
        <v-list-item-content>
          <v-list-item-title :style="{ 'color': appColor.textDark }">
            <span class="card__list__title">
              {{ doc.name }}
              <v-icon
                v-if="showWrongFormatIcon(doc)"
                class="card__list__title__icon"
                color="error"
              >
                bi-exclamation
              </v-icon>
            </span>
          </v-list-item-title>
          <v-list-item-subtitle>Не проверен</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-icon class="card__list-icon my-3">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn
                icon
                :ripple="false"
                :color="appColor.error"
                v-on="on"
                @click.stop="$emit('deleteClick', doc.id)"
              >
                <v-icon :color="appColor.icon">
                  bi-x
                </v-icon>
              </v-btn>
            </template>
            <span>Удалить файл</span>
          </v-tooltip>
        </v-list-item-icon>
      </v-list-item>
    </v-list>
    <v-card-actions class="card__actions">
      <div
        v-if="certificateFile || settingsList?.length"
        class="card__plugins mt-2"
        :class="{
          'mb-2': !settingsList?.length,
          'mb-5': settingsList?.length
        }"
      >
        <v-list-item
          v-if="certificateFile"
          class="card__certificate-field py-0 pl-6 pr-4"
        >
          <v-list-item-content>
            <v-list-item-title :style="{ 'color': appColor.textDark }">
              <span class="card__list__title">
                {{ certificateFile.name }}
                <v-icon
                  v-if="showNotCertIcon(certificateFile)"
                  class="card__list__title__icon"
                  color="error"
                >
                  bi-exclamation
                </v-icon>
              </span>
            </v-list-item-title>
            <v-list-item-subtitle
              class="card__certificate-text"
              :style="{ 'color': appColor.success }"
            >
              Присоединенный сертификат
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-icon class="card__list-icon">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  :color="appColor.error"
                  v-on="on"
                  @click.stop="$emit('deleteCertificate')"
                >
                  <v-icon :color="appColor.icon">
                    bi-x
                  </v-icon>
                </v-btn>
              </template>
              <span>Удалить сертификат</span>
            </v-tooltip>
          </v-list-item-icon>
        </v-list-item>
        <v-checkbox
          v-for="setting in settingsList"
          :key="setting.id"
          v-model="setting.value"
          class="align-self-start mt-2 px-4"
          :color="appColor.secondary"
          hide-details="auto"
          @change="$emit('input', settingsList)"
        >
          <template #label>
            <div
              class="pl-1"
              style="opacity: 0.9"
              :style="{ 'color': `${appColor.textDark} !important` }"
            >
              {{ setting.label }}
            </div>
          </template>
        </v-checkbox>
      </div>
      <v-btn
        class="card__btn"
        :color="`${appColor.secondary} !important`"
        @click="$emit('submit')"
      >
        Проверить
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { fileExtension, isArray } from '@/helpers/helpers'
import { FILE_FORMATS } from '@/helpers/constants'

export default {
  name: 'DocumentList',

  props: {
    docList: {
      type: Array,
      require: true,
      default: () => []
    },
    certificateFile: {
      type: File,
      default: null,
      require: false
    },
    loading: {
      type: Boolean,
      default: false,
      require: false
    },
    selectedId: {
      type: String,
      default: '',
      require: false
    },
    checkForFormat: {
      type: String,
      default: '',
      require: false
    }
  },

  data () {
    return {
      settingsList: [],
      appColor: this.$appConfig.color,
      dragover: false
    }
  },

  computed: {
    ...mapState(['policies']),

    reversedList () {
      return [...this.docList].reverse()
    }
  },

  created () {
    const certSettings = this.policies.CertificateVerifiers
    if (isArray(certSettings)) {
      this.settingsList = certSettings.map(item => {
        return {
          id: item.ID,
          label: item.PluginDescription,
          value: item.CheckByDefaultRequired || false
        }
      })
    } else {
      this.settingsList = []
    }
    this.$emit('input', this.settingsList)
  },

  methods: {
    showWrongFormatIcon (file) {
      if (!this.checkForFormat.length) {
        return false
      }

      const formats = this.checkForFormat.split(', ')
      return formats.length && !formats.includes(fileExtension(file))
    },

    showNotCertIcon (file) {
      const formats = FILE_FORMATS.CERTIFICATES.split(', ')
      return !formats.includes(fileExtension(file))
    },

    onFilesDrop (event) {
      this.dragover = false
      this.$emit('dropUpload', event.dataTransfer.files)
    }
  }
}
</script>

<style lang="scss" scoped>
  .card {
    border-radius: 8px;

    &__toolbar {
      border-bottom: 1px solid $color-divider;
    }

    &__title {
      position: absolute;
      left: 50%;
      top: 50%;
      padding: 0 !important;
      transform: translate(-50%, -50%);

      @media(max-width: $cp-laptop-breakpoint) {
        font-size: 1.1rem;
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      border-top: 1px solid $color-divider;
    }

    &__trash-btn {
      margin-right: -5px;
    }

    &__btn {
      width: 100%;
      height: 60px !important;
      border-radius: 0 0 8px 8px;

      @include cp-button;
    }

    &__plugins {
      width: 100%;
    }

    &__certificate-field {
      width: 100%;
    }

    &__certificate-text {
      color: $color-success;
    }

    &__list-icon {
      align-self: inherit;
      margin: 0;
    }

    &__list {
      height: 192px;
      padding: 0;
      overflow-y: auto;

      &--small {
        height: 192px;
      }

      &--medium {
        height: 310px;
      }

      &--auto {
        height: 100%;
        min-height: 192px;
        max-height: 384px;
      }

      &__title {
        position: relative;

        &__icon {
          position: absolute;
          right: -24px;
          top: 50%;
          transform: translateY(-52%);
          font-size: 27px;
        }
      }
    }

    &__item {
      &--active {
        background-color: $color-accent;
      }
    }
  }
</style>
